<!-- SyntheticTransactionModal.vue -->
<template>
  <div v-if="isVisible" class="flex items-center justify-center fixed inset-0 bg-black/50 backdrop-blur-sm z-40">
    <div class="p-6 glass-card mb-2 w-[80%] shadow-xl z-50 bg-white dark:bg-gray-800">
      <!-- Header -->
      <div class="mb-8">
        <h2 class="font-semibold mb-4 text-gray-800 dark:text-gray-200 capitalize">
          Synthetic Transaction
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="flex flex-col" v-for="(label, field) in fieldLabels" :key="field">
            <label :for="field" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
              {{ label }}<span class="text-red-500">*</span>:
            </label>

            <!-- Protocol Dropdown -->
            <div v-if="field === 'protocol'" class="flex flex-col">
              <GlassSelect
                v-model="modelValue[field]"
                @change="$emit('protocol-change', modelValue[field])"
                id="protocol"
                :options="protocols"
                placeholder="Select Protocol"
                :error="!!errors[field]"
              />
              <FieldError :message="errors?.protocol"/>
            </div>

            <!-- DNS Servers Field -->
            <div v-else-if="field === 'servers'" class="flex flex-col">
              <div class="space-y-2">
                <div class="flex items-center space-x-2">
                  <input
                    v-model="serverInput"
                    id="server_url"
                    type="text"
                    placeholder="9.9.9.9"
                    @keyup.enter="addServer"
                    @focus="clearFieldError(field)"
                    :class="[
                      'flex-1 border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2',
                      errors?.[field] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    ]"
                  />
                  <button
                    type="button"
                    @click="addServer"
                    class="btn bg-green-600 text-white hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>
                <FieldError :message="errors?.[field]" />
                <ul class="flex flex-wrap gap-2 mt-2">
                  <li 
                    v-for="(server, index) in modelValue.servers"
                    :key="index"
                    class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {{ server }}
                    </span>
                    <button
                      @click="removeServer(server)"
                      type="button"
                      class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none transition-colors duration-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            <!-- DNS Domains Field -->
            <div v-else-if="field === 'domains'" class="flex flex-col">
              <div class="space-y-2">
                <div class="flex items-center space-x-2">
                  <input
                    v-model="domainInput"
                    id="domain_url"
                    type="text"
                    placeholder="endpoint.example.com"
                    @focus="clearFieldError('domains')"
                    @keyup.enter="addDomain"
                    :class="[
                      'flex-1 border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2',
                      errors?.[field] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    ]"
                  />
                  <button
                    type="button"
                    @click="addDomain"
                    class="btn bg-green-600 text-white hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>
                <FieldError :message="errors?.[field]" />
                <ul class="flex flex-wrap gap-2 mt-2">
                  <li 
                    v-for="(domain, index) in modelValue.domains"
                    :key="index"
                    class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <span class="shadow-sm text-sm font-medium dark:text-gray-50 truncate">
                      {{ domain }}
                    </span>
                    <button
                      @click="removeDomain(domain)"
                      type="button"
                      class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none transition-colors duration-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            <!-- URLs Field -->
            <div v-else-if="field === 'urls'" class="flex flex-col">
              <div class="space-y-2">
                <div class="flex items-center space-x-2">
                  <input
                    v-model="urlInput"
                    id="synth_url"
                    type="url"
                    placeholder="endpoint.example.com"
                    @keyup.enter="addURL"
                    :class="[
                      'flex-1 border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2',
                      errors?.[field] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    ]"
                  />
                  <button
                    type="button"
                    @click="addURL"
                    class="btn bg-green-600 text-white hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>
                <FieldError :message="errors?.[field]" />
                <ul class="flex flex-wrap gap-2 mt-2">
                  <li 
                    v-for="(url, index) in modelValue.urls"
                    :key="index"
                    class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {{ url }}
                    </span>
                    <button
                      @click="removeURL(url)"
                      type="button"
                      class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none transition-colors duration-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Timeout Number Field -->
            <div v-else-if="field === 'timeout'" class="flex flex-col">
              <input
                :id="field"
                v-model.number="modelValue[field]"
                @focus="clearFieldError(field)"
                type="number"
                min="1"
                :placeholder="'Enter ' + label"
                :class="[
                  'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
                  errors?.[field] ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                ]"
              />
              <FieldError :message="errors?.[field]" />
            </div>

            <!-- Regular Text Input Fields -->
            <div v-else class="flex flex-col">
              <input
                :id="field"
                v-model="modelValue[field]"
                @focus="clearFieldError(field)"
                :placeholder="'Enter ' + label"
                :class="[
                  'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
                  errors?.[field] ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                ]"
              />
              <FieldError :message="errors?.[field]" />
            </div>
          </div>
          
          <!-- Non-field errors -->
          <div class="col-span-full">
            <FieldError :message="errors?.non_field_errors"/>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-between">
        <div>
          <button 
            v-if="canSave"
            type="button"
            id="save_transaction"
            @click="$emit('save')"
            class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
          >
            Save
          </button>
          <button
            type="button"
            @click="$emit('cancel')"
            class="btn ml-2 bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
          >
            Cancel
          </button>
        </div>
        <div>
          <button 
            v-if="modelValue.id"
            type="button"
            @click="$emit('delete')"
            class="btn bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import FieldError from '../FieldError.vue'
import GlassSelect from "@/components/GlassSelect.vue"

const props = defineProps({
  isVisible: {
    type: Boolean,
    required: true
  },
  modelValue: {
    type: Object,
    required: true
  },
  fieldLabels: {
    type: Object,
    required: true
  },
  protocols: {
    type: Array,
    required: true
  },
  configType: {
    type: String,
    required: true
  },
  isGeneralFileUpload: {
    type: Boolean,
    default: false
  },
  canSave: {
    type: Boolean,
    default: false
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue', 'protocol-change', 'save', 'cancel', 'delete'])

const serverInput = ref('')
const domainInput = ref('')
const urlInput = ref('')

const isValidIPv4 = (ip) => {
  const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
  return ipv4Regex.test(ip)
}

const hasProtocol = (value) => /^[a-zA-Z]+[:;][\/\\]{0,2}/.test(value)
const hasPort = (value) => /:(\d+)$/.test(value)
const hasSemicolon = (value) => /[;]/.test(value)

const hasProtocolTypo = (value) => {
  const hasTypo = /^h+t*p+s*:\/\//i.test(value)
  const isValid = /^https?:\/\//i.test(value)
  return hasTypo && !isValid
}


const validateURL = (value) => {
  const protocol = props.modelValue.protocol

  if (protocol === 'icmp') {
    if (hasProtocol(value)) {
      return "Please enter the hostname only (e.g., 'example.org')"
    }
    if (hasPort(value)) {
      return "Please enter the url without the port number"
    }
  }

  if (protocol === 'http' || protocol === 'https') {
    if (hasSemicolon(value)) {
      return "Incorrect protocol format - check for typos"
    }

    if (hasProtocolTypo(value)) {
      return "Incorrect protocol format - check for typos (e.g., htps, htttps)"
    }

    const oppositeProtocol = protocol === 'http' ? 'https://' : 'http://'
    if (value.startsWith(oppositeProtocol)) {
      return "Incorrect protocol"
    }
  }

  return null
}

const validateInput = (value, type) => {
  switch(type) {
    case 'servers':
      return !isValidIPv4(value) 
        ? "Please enter a valid IPv4 address (e.g., 192.168.1.1)" 
        : null

    case 'domains':
      if (hasProtocol(value)) {
        return "Please enter the domain without http:// or https://"
      }
      if (hasPort(value)) {
        return "Please enter the domain without the port number"
      }
      return null

    case 'urls':
      return validateURL(value)

    default:
      return null
  }
}

const transformInput = (value, type) => {
  if (type === 'urls') {
    const protocol = props.modelValue.protocol
    
    if ((protocol === 'http' || protocol === 'https') &&
        !value.startsWith('http://') && 
        !value.startsWith('https://')) {
      return `${protocol}://${value}`
    }
  }
  
  return value
}


const addToList = (value, inputRef, listKey, errorKey) => {
  props.errors[errorKey] = ''
  
  if (!value?.trim()) return

  const error = validateInput(value, listKey)
  if (error) {
    props.errors[errorKey] = error
    return
  }

  const transformedValue = transformInput(value, listKey)
  
  emit('update:modelValue', {
    ...props.modelValue,
    [listKey]: [...(props.modelValue[listKey] || []), transformedValue]
  })
  

  switch(inputRef) {
    case 'serverInput':
      serverInput.value = ''
      break
    case 'domainInput':
      domainInput.value = ''
      break
    case 'urlInput':
      urlInput.value = ''
      break
  }
}

const removeFromList = (item, listKey, errorKey) => {
  props.errors[errorKey] = ''
  emit('update:modelValue', {
    ...props.modelValue,
    [listKey]: props.modelValue[listKey].filter(i => i !== item)
  })
}

const addServer = () => addToList(serverInput.value, 'serverInput', 'servers', 'servers')
const removeServer = (server) => removeFromList(server, 'servers', 'servers')


const addDomain = () => addToList(domainInput.value, 'domainInput', 'domains', 'domains')
const removeDomain = (domain) => removeFromList(domain, 'domains', 'domains')


const addURL = () => addToList(urlInput.value, 'urlInput', 'urls', 'urls')
const removeURL = (url) => removeFromList(url, 'urls', 'urls')

const clearFieldError = (fieldName) => {
    if (props.errors[fieldName]) {
      delete props.errors[fieldName];
      delete props.errors['non_field_errors'];
    }
  }
</script>